import { useState } from "react";
import { Icon } from "@iconify/react";
import { useTranslation } from "../../i18n";
import { post } from "../../utils/api";

const TITLE_PILLS = [
  { en: "Vacation", ru: "Отпуск", he: "חופשה" },
  { en: "Day off", ru: "Выходной", he: "יום חופש" },
  { en: "Renovation", ru: "Ремонт", he: "שיפוץ" },
  { en: "Holiday", ru: "Праздник", he: "חג" },
];

const NOTIFY_OPTIONS = [
  { value: 1, en: "1 day before", ru: "За 1 день", he: "יום לפני" },
  { value: 2, en: "2 days before", ru: "За 2 дня", he: "יומיים לפני" },
  { value: 3, en: "3 days before", ru: "За 3 дня", he: "3 ימים לפני" },
  { value: 7, en: "1 week before", ru: "За 1 неделю", he: "שבוע לפני" },
  { value: 14, en: "2 weeks before", ru: "За 2 недели", he: "שבועיים לפני" },
];

const RECURRENCE_OPTIONS = [
  { value: "none", en: "None", ru: "Нет", he: "ללא" },
  { value: "weekly", en: "Weekly", ru: "Еженедельно", he: "שבועי" },
  { value: "monthly", en: "Monthly", ru: "Ежемесячно", he: "חודשי" },
  { value: "yearly", en: "Yearly", ru: "Ежегодно", he: "שנתי" },
];

export default function DayOffModal({ isOpen, onClose, businessId, onCreated }) {
  const { lang } = useTranslation();

  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [allDay, setAllDay] = useState(true);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("18:00");
  const [affects, setAffects] = useState("entire");
  const [reason, setReason] = useState("");
  const [notifyBefore, setNotifyBefore] = useState(1);
  const [recurrence, setRecurrence] = useState("none");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const l = (en, ru, he) => (lang === "ru" ? ru : lang === "he" ? he : en);

  const reset = () => {
    setTitle(""); setStartDate(""); setEndDate(""); setAllDay(true);
    setStartTime("09:00"); setEndTime("18:00"); setAffects("entire");
    setReason(""); setNotifyBefore(1); setRecurrence("none");
    setError("");
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSave = async () => {
    if (!title.trim()) { setError(l("Title is required", "Название обязательно", "נדרשת כותרת")); return; }
    if (!startDate) { setError(l("Start date is required", "Дата начала обязательна", "נדרש תאריך התחלה")); return; }

    setSaving(true);
    setError("");
    try {
      await post(`/api/businesses/${businessId}/day-offs`, {
        title: title.trim(),
        start_date: startDate,
        end_date: endDate || startDate,
        all_day: allDay,
        start_time: allDay ? null : startTime,
        end_time: allDay ? null : endTime,
        affects,
        reason: reason.trim() || null,
        notify_before_days: notifyBefore,
        recurrence,
      });
      reset();
      onCreated?.();
      onClose();
    } catch (err) {
      setError(err.message || l("Failed to save", "Ошибка сохранения", "שגיאה בשמירה"));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            {l("Add Day Off", "Добавить выходной", "הוסף יום חופש")}
          </h2>
          <button onClick={handleClose} className="p-1 rounded-lg hover:bg-gray-100 transition-colors">
            <Icon icon="mdi:close" className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 flex flex-col gap-5">

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {l("Title", "Название", "כותרת")}
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {TITLE_PILLS.map((p) => {
                const label = p[lang] || p.en;
                const active = title === label;
                return (
                  <button
                    key={p.en}
                    type="button"
                    onClick={() => setTitle(label)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      active
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={l("Custom title...", "Свое название...", "כותרת מותאמת...")}
              className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:border-blue-400 transition-colors"
            />
          </div>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {l("Start date", "Дата начала", "תאריך התחלה")}
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:border-blue-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {l("End date", "Дата окончания", "תאריך סיום")}
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:border-blue-400"
              />
            </div>
          </div>

          {/* All day toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {l("All day", "Весь день", "כל היום")}
            </span>
            <button
              type="button"
              onClick={() => setAllDay(!allDay)}
              className={`relative w-10 h-5 rounded-full transition-colors ${allDay ? "bg-blue-600" : "bg-gray-300"}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${allDay ? "left-5.5 translate-x-0.5" : "left-0.5"}`} />
            </button>
          </div>

          {/* Time range (if not all day) */}
          {!allDay && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {l("Start time", "Время начала", "שעת התחלה")}
                </label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:border-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {l("End time", "Время окончания", "שעת סיום")}
                </label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:border-blue-400"
                />
              </div>
            </div>
          )}

          {/* Affects */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {l("Affects", "Применяется к", "משפיע על")}
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="affects" value="entire" checked={affects === "entire"} onChange={() => setAffects("entire")} className="w-4 h-4 accent-blue-600" />
                <span className="text-sm text-gray-600">{l("Entire business", "Весь бизнес", "כל העסק")}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="affects" value="specific" checked={affects === "specific"} onChange={() => setAffects("specific")} className="w-4 h-4 accent-blue-600" />
                <span className="text-sm text-gray-600">{l("Specific staff", "Конкретные сотрудники", "צוות ספציפי")}</span>
              </label>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {l("Reason (optional)", "Причина (необязательно)", "סיבה (אופציונלי)")}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder={l("Add a note...", "Добавить заметку...", "הוסף הערה...")}
              className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:border-blue-400 resize-none"
            />
          </div>

          {/* Notify before */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {l("Notify before", "Уведомить за", "הודע לפני")}
            </label>
            <select
              value={notifyBefore}
              onChange={(e) => setNotifyBefore(Number(e.target.value))}
              className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:border-blue-400 bg-white"
            >
              {NOTIFY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o[lang] || o.en}</option>
              ))}
            </select>
          </div>

          {/* Recurrence */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {l("Recurrence", "Повторение", "חזרה")}
            </label>
            <select
              value={recurrence}
              onChange={(e) => setRecurrence(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:border-blue-400 bg-white"
            >
              {RECURRENCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o[lang] || o.en}</option>
              ))}
            </select>
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-500 flex items-center gap-1.5">
              <Icon icon="mdi:alert-circle" className="w-4 h-4" />
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button
            onClick={handleClose}
            className="px-5 py-2 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            {l("Cancel", "Отмена", "ביטול")}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 rounded-xl text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {saving && <Icon icon="mdi:loading" className="w-4 h-4 animate-spin" />}
            {l("Save", "Сохранить", "שמור")}
          </button>
        </div>
      </div>
    </div>
  );
}
